import { MainTabsV2 } from '@labrav/react-components'
import { scrollspyTabs, ScrollToFieldError, useSalesforceContext } from '@labrav/common'
import { useStyles } from './ResellerAuthorizationCreationForm.styles'
import { useTheme } from '@mui/material'
import { ResellerMicrosoftOfferDetail as MicrosoftOfferDetail } from '../../../PrivateOfferFormV2/AzureOffer/ResellerMicrosoftOfferDetail'
import { CustomMetaData } from '../../../PrivateOfferFormV2/AzureOffer/CustomMetaData/CustomMetaData'
import { Stakeholders } from '../../../PrivateOfferFormV2/AzureOffer/Stakeholders/Stakeholders'
import { Renewal } from '../../../PrivateOfferFormV2/AzureOffer/Renewal/Renewal'
import { ContractDuration } from '../../../PrivateOfferFormV2/AzureOffer/ContractDuration/ContractDuration'
import { ResellerInfo } from '../../../PrivateOfferFormV2/AzureOffer/ResellerInfo/ResellerInfo'
import { Eula } from '../../../PrivateOfferFormV2/AzureOffer/Eula/Eula'
import { Plan } from '../../../PrivateOfferFormV2/AzureOffer/Plan/Plan'
import { BuyerNotification } from '../../../PrivateOfferFormV2/AzureOffer/BuyerNotification/BuyerNotification'
import { getIsErrorForMPPOOfferTab } from '../../../PrivateOfferFormV2/AzureOffer/getIsErrorForMPPOOfferTab'
import { useFormikContext } from 'formik'
import { useSelector } from '../../../../store'
import {
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'
import { MarketplaceTemplateDataResponse } from '../../../../modules/marketplaceMappingManager/types'
import { useMemo, useState } from 'react'
import type { ResellerAuthorizationCreationFormProps } from '../../types'
import { isEmpty } from 'lodash'

export const ResellerAuthorizationCreationForm = ({
  product,
  tabTop,
  offset = 260,
  users,
  externalBaseURL,
}: ResellerAuthorizationCreationFormProps) => {
  const { classes } = useStyles({ offset })
  const theme = useTheme()
  const salesforceContext = useSalesforceContext()
  const isSalesforceContext =
    salesforceContext.error !== 'salesforce_context_not_available' &&
    !isEmpty(salesforceContext.partnerId)
  const formikProps = useFormikContext<Record<string, unknown>>()
  const [eulaErr, setEulaErr] = useState<string>('')

  const templateData = useSelector<MarketplaceTemplateDataResponse | undefined>(
    state => state.marketplaceMappingManager?.templateData?.templateData
  )
  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )

  const fieldMetadataMetaData =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Metadata_Hide__c
      ?.isHidden
  const fieldMetadataStakeholders =
    templateData?.fieldMappings?.fields?.fieldsMapping?.Labra__Stakeholders__c
      ?.isHidden

  const isEulaFileReadOnly = (fieldMetadata?.eulaFiles as FieldMetadataField)
    ?.readOnly
  const isEulaFileHidden = (fieldMetadata?.eulaFiles as FieldMetadataField)
    ?.hidden
  const isEulaTypeReadOnly = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.readOnly
  const isEulaTypeHidden = (fieldMetadata?.eulaType as FieldMetadataField)
    ?.hidden

  const shouldRenderEulaSection = useMemo(
    () => !(isEulaFileHidden && isEulaTypeHidden),
    [isEulaFileHidden, isEulaTypeHidden]
  )
  const isRenewalHidden = (
    fieldMetadata?.existingContractRenewal as FieldMetadataField
  )?.hidden

  const isResellerNameHidden = (
    fieldMetadata?.resellerName as FieldMetadataField
  )?.hidden
  const isResellerAccountIdHidden = (
    fieldMetadata?.resellerAccountId as FieldMetadataField
  )?.hidden

  const shouldRenderResellerInfoSection = useMemo(
    () => !(isResellerNameHidden && isResellerAccountIdHidden),
    [isResellerNameHidden, isResellerAccountIdHidden]
  )
  const resellerAuthorizationCreationTabs = [
    {
      key: 'offer-details',
      label: 'Reseller authorization',
      renderElement: (
        <div
          id={'offer-details'}
          data-testid={'offer-details'}
          className={classes.scrollTopMargin}
        >
          <MicrosoftOfferDetail formikProps={formikProps} product={product} />
          {shouldRenderResellerInfoSection && (
            <ResellerInfo product={product} />
          )}
          <BuyerNotification />
          <ContractDuration product={product} fieldMetadata={fieldMetadata} />
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-details',
        formikValues: formikProps,
      }),
    },
    {
      key: 'offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div
          id={'offer-pricing'}
          data-testid={'offer-pricing'}
          className={classes.scrollTopMargin}
        >
          <Plan product={product} />
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-pricing',
        formikValues: formikProps,
      }),
    },
    {
      key: 'offer-other',
      label: 'Other',
      renderElement: (
        <div
          id={'offer-other'}
          data-testid={'offer-other'}
          className={classes.scrollTopMargin}
        >
          {!isRenewalHidden && <Renewal product={product} />}
          {shouldRenderEulaSection && (
            <Eula
              eulaErr={eulaErr}
              setEulaErr={setEulaErr}
              showEulaOptions={isEulaTypeHidden ? false : true}
              disableEulaFileUpload={isEulaFileReadOnly}
              showEulaFileUpload={isEulaFileHidden ? false : true}
              disableEulaOptions={isEulaTypeReadOnly}
            />
          )}
          {!fieldMetadataStakeholders && (
            <Stakeholders
              hideStakeholders={!!isSalesforceContext || !!externalBaseURL}
              users={users}
            />
          )}
          {!fieldMetadataMetaData && <CustomMetaData product={product} />}
        </div>
      ),
      isError: getIsErrorForMPPOOfferTab({
        tabName: 'offer-other',
        formikValues: formikProps,
        eulaErr,
      }),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: resellerAuthorizationCreationTabs,
    offset: offset,
  })

  return (
    <>
      <ScrollToFieldError customErrorFields={eulaErr ? ['eulaFile'] : []} />
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={resellerAuthorizationCreationTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={tabTop}
      />
      {resellerAuthorizationCreationTabs.map(tabItem => tabItem.renderElement)}
    </>
  )
}
